import { Flex } from '@chakra-ui/react';
import { useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import { useDispatch } from 'react-redux';
import { checkNewCompetencyPanelEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { Button } from 'uiToolkitV1/Button';

import { setLabelSettings, setViewType, useCompetencySettings } from '../competencySettingsSlice';
import {
  useGetCompetencyLabelsQuery,
  useGetCompetencySettingsQuery,
  useUpdateCompetencyLabelsMutation,
  useUpdateCompetencySettingsMutation,
} from './dataHooks';
import { isScorePercentValidator } from './helpers/isScorePercentValidator';
import { Label } from './types/labels';

export const CompetencySettingsFooter = ({ setShowSettingsScreen }) => {
  const { data: competencySettings } = useGetCompetencySettingsQuery();
  const { data: competencyLabels } = useGetCompetencyLabelsQuery();
  const { viewType, labelSettings } = useCompetencySettings();
  const { mutate: updateCompetencySettings } = useUpdateCompetencySettingsMutation();
  const { mutate: updateCompetencyLabels } = useUpdateCompetencyLabelsMutation();
  const dispatch = useDispatch();

  const isPercentInvalid = checkNewCompetencyPanelEnabled() ? isScorePercentValidator(labelSettings) : false;

  const isSidebarVisible = useSelectIsL2Visible();

  const hasCompetencySettingsChanged = competencySettings?.viewType !== viewType;
  const hasCompetencyLabelsChanged =
    checkNewCompetencyPanelEnabled() &&
    (competencyLabels?.answerOptions.length !== labelSettings.length ||
      competencyLabels?.answerOptions.some(
        (option, i) =>
          Number(option.score) !== Number(labelSettings[i]?.upperLimit) ||
          option.description !== labelSettings[i]?.label
      ));

  const saveCompetencySettings = () => {
    updateCompetencySettings(
      { viewType },
      {
        onSuccess: () => {
          if (!checkNewNavEnabled()) {
            setShowSettingsScreen(false);
          }
        },
      }
    );
  };

  const saveCompetencyLabels = () => {
    updateCompetencyLabels(
      {
        label: {
          ...competencyLabels,
          answerOptions: labelSettings.map((setting, i) => ({
            description: setting.label,
            score: setting.upperLimit,
            displayOrder: i + 1,
          })),
        },
      },
      {
        onSuccess: (data: Label) => {
          if (!checkNewNavEnabled()) {
            setShowSettingsScreen(false);
          }
          dispatch(
            setLabelSettings(
              data.answerOptions.map((option) => ({ upperLimit: option.score, label: option.description }))
            )
          );
        },
      }
    );
  };

  const onSave = () => {
    if (hasCompetencySettingsChanged) {
      saveCompetencySettings();
    }
    if (hasCompetencyLabelsChanged) {
      saveCompetencyLabels();
    }
  };

  const onCancel = () => {
    if (hasCompetencySettingsChanged) {
      dispatch(setViewType(competencySettings.viewType));
    }
    if (hasCompetencyLabelsChanged) {
      dispatch(
        setLabelSettings(
          competencyLabels.answerOptions.map((option) => ({ upperLimit: option.score, label: option.description }))
        )
      );
    }
    if (!checkNewNavEnabled()) {
      setShowSettingsScreen(false);
    }
  };

  // eslint-disable-next-line no-nested-ternary
  const leftPos = checkNewNavEnabled() ? (isSidebarVisible ? '320px' : '80px') : '19%';

  return (
    <Flex
      h="72px"
      backgroundColor="neutral.F5F7F9"
      p={4}
      borderTopWidth="1px"
      pos="fixed"
      left={leftPos}
      right={0}
      bottom={0}
    >
      <Button
        variant="tertiary"
        mr="auto"
        isDisabled={!hasCompetencyLabelsChanged && !hasCompetencySettingsChanged}
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button
        isDisabled={isPercentInvalid || (!hasCompetencyLabelsChanged && !hasCompetencySettingsChanged)}
        onClick={onSave}
      >
        Save
      </Button>
    </Flex>
  );
};
